// Axios client + auto-refresh interceptor. Backend talks via HttpOnly cookies in
// production but also accepts Bearer headers; in this client we use cookies (withCredentials)
// so we don't have to store tokens in localStorage (XSS-safe).

import axios, { AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';

export type ApiErrorShape = {
  error: { code: string; message: string; details?: unknown };
  requestId?: string;
};

export class ApiError extends Error {
  status: number;
  code: string;
  details?: unknown;
  requestId?: string;
  constructor(status: number, code: string, message: string, details?: unknown, requestId?: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
    this.requestId = requestId;
  }
}

// Human-friendly labels for the backend's dotted field paths (e.g. "address.stateCode").
// Only the ones users actually see in a form need an entry; the fallback prettifies the rest.
const FIELD_LABELS: Record<string, string> = {
  name: 'Company name',
  legalName: 'Legal name',
  pan: 'PAN',
  gstin: 'GSTIN',
  cin: 'CIN',
  contactEmail: 'Contact email',
  contactPhone: 'Contact phone',
  websiteUrl: 'Website',
  logoUrl: 'Logo URL',
  'address.line1': 'Address line 1',
  'address.line2': 'Address line 2',
  'address.city': 'City',
  'address.state': 'State',
  'address.stateCode': 'State code',
  'address.pincode': 'PIN code',
  fiscalYearStart: 'Financial year start',
  baseCurrency: 'Base currency',
};

function prettifyPath(path: string): string {
  if (FIELD_LABELS[path]) return FIELD_LABELS[path];
  const leaf = path.split('.').pop() ?? path;
  // camelCase → "Camel case", then capitalise
  const spaced = leaf.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/[._]/g, ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/**
 * Turn an ApiError into a message worth showing a user.
 *
 * The backend already returns per-field reasons for a 400 validation failure
 * (`details.fields`), but a bare `err.message` is just "Validation failed" — a
 * dead end. This expands it into "Company name: too short · State code: invalid…"
 * so the person knows exactly what to fix. Non-validation errors fall through to
 * the plain message.
 */
export function fieldErrorMessage(err: unknown, fallback = 'Something went wrong. Please try again.'): string {
  if (!(err instanceof ApiError)) {
    return err instanceof Error ? err.message : fallback;
  }
  const fields = (err.details as { fields?: Record<string, string[]> } | undefined)?.fields;
  if (fields && typeof fields === 'object') {
    const parts = Object.entries(fields)
      .filter(([, msgs]) => Array.isArray(msgs) && msgs.length > 0)
      .map(([path, msgs]) => `${prettifyPath(path === '_root' ? '' : path)}: ${msgs[0]}`.replace(/^: /, ''));
    if (parts.length > 0) return parts.join(' · ');
  }
  return err.message || fallback;
}

function buildClient(): AxiosInstance {
  const client = axios.create({
    baseURL: '/api',
    withCredentials: true,
    timeout: 30_000,
    headers: { 'X-Client': 'orenza-web' },
  });

  let isRefreshing = false;
  // B-011: the second arg carries the REFRESH failure so queued callers reject with
  // the real cause instead of their own stale 401.
  let pending: Array<(token: string | null, refreshError?: unknown) => void> = [];

  client.interceptors.response.use(
    (response) => response,
    async (error: AxiosError<ApiErrorShape>) => {
      const original = error.config as InternalAxiosRequestConfig & { _retried?: boolean };
      const status = error.response?.status;
      const code = error.response?.data?.error?.code;

      // Try one silent refresh on 401 — but only when a session plausibly
      // exists. The signal is the SERVER-set non-HttpOnly 'orenza_session'
      // cookie, which lives exactly as long as the refresh cookie. (The old
      // localStorage flag could be absent while a valid 7-day refresh cookie
      // existed — pre-flag logins, cleared storage — hard-logging users out.
      // localStorage is kept as a fallback signal for sessions from that era.)
      let hadSession = /(?:^|;\s*)orenza_session=1(?:;|$)/.test(document.cookie ?? '');
      if (!hadSession) {
        try { hadSession = localStorage.getItem('orenza:session') === '1'; } catch { /* private mode */ }
      }
      if (
        status === 401 &&
        (code === 'auth.token-expired' || (code === 'auth.no-token' && hadSession)) &&
        original &&
        !original._retried &&
        !original.url?.includes('/auth/refresh') &&
        !original.url?.includes('/auth/login')
      ) {
        original._retried = true;

        if (isRefreshing) {
          // Queue. B-011: on failure, reject with the REFRESH error, not this
          // request's original 401 — otherwise every queued call reports
          // "token expired" and the actual cause (network timeout, 500 from
          // /auth/refresh, revoked session) never appears in the logs.
          return new Promise((resolve, reject) => {
            pending.push((token, refreshError) => {
              if (token === null) return reject(refreshError ?? error);
              resolve(client(original));
            });
          });
        }

        isRefreshing = true;
        try {
          // Explicit timeout: this call runs on the bare axios instance (no
          // client defaults). A black-holed refresh used to hold the mutex
          // forever — every queued request hung and sign-in span "Signing in…"
          // until a manual reload.
          await axios.post('/api/auth/refresh', undefined, { withCredentials: true, timeout: 10_000 });
          pending.forEach((cb) => cb('refreshed'));
          pending = [];
          return client(original);
        } catch (refreshError) {
          pending.forEach((cb) => cb(null, refreshError));
          pending = [];
          // Hard log-out signal — listener in auth store redirects to /login
          window.dispatchEvent(new CustomEvent('orenza:auth-expired'));
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }

      // Wrap errors in our typed ApiError for callers
      if (error.response) {
        const payload = error.response.data;
        return Promise.reject(
          new ApiError(
            error.response.status,
            payload?.error?.code ?? 'unknown',
            payload?.error?.message ?? error.message,
            payload?.error?.details,
            payload?.requestId,
          ),
        );
      }
      return Promise.reject(error);
    },
  );

  return client;
}

export const api = buildClient();
